import { useLocation, Link } from "react-router-dom";
import { 
  LayoutDashboard, 
  Users, 
  FileText, 
  CreditCard, 
  Settings,
  LogOut,
  UserCog,
  Wrench,
  Package,
  ClipboardList,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  AlertCircle,
  Ruler,
  Filter,
  CalendarClock,
  CalendarPlus,
  Mail
} from "lucide-react";
import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import NotificationBell from "@/components/NotificationBell";
import YearSelector from "@/components/YearSelector";
import { useSidebar } from "@/contexts/SidebarContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const menuItems = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/attenzionati", label: "Attenzionati", icon: AlertCircle, hasBadge: true },
  { path: "/appuntamenti", label: "Appuntamenti", icon: CalendarClock },
  { path: "/interviews", label: "Interviste", icon: Users },
  { path: "/offers", label: "Offerte", icon: FileText },
  { path: "/funnel-ripresa", label: "Follow UP", icon: Filter },
  { path: "/rilievo-misure", label: "Rilievo Misure", icon: Ruler, hasBadge: true, badgeKey: "rilievoMisure" },
  { path: "/ordini", label: "Contratti", icon: Package },
  { path: "/payments", label: "Pagamenti", icon: CreditCard },
  { path: "/crono-ordini", label: "Crono Ordini", icon: ClipboardList, hasBadge: true, badgeKey: "cronoOrdini" },
  { path: "/commesse-pose", label: "Commesse e Pose", icon: Wrench },
  { path: "/verifica-completamenti", label: "Verifica Completamenti", icon: AlertTriangle, hasBadge: true },
  { path: "/pose-magazzino", label: "Magazzino", icon: Package },
  { path: "/template-email", label: "Template Email", icon: Mail },
  { path: "/calendari-rapidi", label: "Calendari Rapidi", icon: CalendarPlus },
  { path: "/settings", label: "Impostazioni", icon: Settings },
];

export default function Sidebar({ user, logout }) {
  const location = useLocation();
  const [verificaCompletamentiCount, setVerificaCompletamentiCount] = useState(0);
  const [attenzionatiCount, setAttenzionatiCount] = useState(0);
  const [rilievoMisureCount, setRilievoMisureCount] = useState(0);
  const [cronoOrdiniScadutiCount, setCronoOrdiniScadutiCount] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showToggleHint, setShowToggleHint] = useState(false);
  const [isCompactScreen, setIsCompactScreen] = useState(false);
  const { isCollapsed, toggleCollapsed } = useSidebar();
  
  // Rileva se lo schermo è "compatto" (< 1400px larghezza O < 900px altezza)
  useEffect(() => {
    const checkScreenSize = () => {
      const isCompact = window.innerWidth < 1400 || window.innerHeight < 900;
      setIsCompactScreen(isCompact);
      // Chiudi menu quando si passa a schermo grande
      if (!isCompact) {
        setIsMenuOpen(false);
      }
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Mostra animazione bounce al primo accesso (solo su schermi grandi)
  useEffect(() => {
    const hasSeenToggle = localStorage.getItem('sidebar_toggle_seen');
    if (!hasSeenToggle && !isCompactScreen) {
      const timer = setTimeout(() => setShowToggleHint(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [isCompactScreen]);

  const handleToggleClick = () => {
    setShowToggleHint(false);
    localStorage.setItem('sidebar_toggle_seen', 'true');
    toggleCollapsed();
  };
  
  // Fetch verifica completamenti count
  const fetchVerificaCount = async () => {
    try {
      const res = await axios.get(`${API}/commesse-pose/verifica-completamenti/count`, { withCredentials: true });
      setVerificaCompletamentiCount(res.data.count);
    } catch (error) {
      console.error("Error fetching verifica completamenti count:", error);
    }
  };

  // Fetch attenzionati count
  const fetchAttenzionatiCount = async () => {
    try {
      const res = await axios.get(`${API}/attenzionati/count`, { withCredentials: true });
      setAttenzionatiCount(res.data.count);
    } catch (error) {
      console.error("Error fetching attenzionati count:", error);
    }
  };

  // Fetch rilievo misure count (senza data)
  const fetchRilievoMisureCount = async () => {
    try {
      const res = await axios.get(`${API}/rilievo-misure/count-senza-data`, { withCredentials: true });
      setRilievoMisureCount(res.data.count);
    } catch (error) {
      console.error("Error fetching rilievo misure count:", error);
    }
  };

  // Fetch crono ordini scaduti count
  const fetchCronoOrdiniScadutiCount = async () => {
    try {
      const res = await axios.get(`${API}/crono-ordini`, { withCredentials: true });
      const scadutiCount = res.data?.scaduti?.length || 0;
      setCronoOrdiniScadutiCount(scadutiCount);
    } catch (error) {
      console.error("Error fetching crono ordini scaduti count:", error);
    }
  };

  useEffect(() => {
    if (!user) return;
    
    fetchVerificaCount();
    fetchAttenzionatiCount();
    fetchRilievoMisureCount();
    fetchCronoOrdiniScadutiCount();
    const interval = setInterval(() => {
      fetchVerificaCount();
      fetchAttenzionatiCount();
      fetchRilievoMisureCount();
      fetchCronoOrdiniScadutiCount();
    }, 60000);
    return () => clearInterval(interval);
  }, [user]);

  // Chiudi menu quando si cambia pagina
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);
  
  // Aggiungi voce Utenti solo per admin
  const allMenuItems = user?.role === "admin" 
    ? [...menuItems, { path: "/users", label: "Utenti", icon: UserCog }]
    : menuItems;

  // Componente MenuItem riutilizzabile
  const MenuItem = ({ item, showLabel = true, onClick }) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.path;
    const isVerificaCompletamenti = item.path === "/verifica-completamenti";
    const isAttenzionati = item.path === "/attenzionati";
    const isRilievoMisure = item.path === "/rilievo-misure";
    const isCronoOrdini = item.path === "/crono-ordini";
    
    // Determina il badge count
    let badgeCount = 0;
    if (isVerificaCompletamenti) badgeCount = verificaCompletamentiCount;
    if (isAttenzionati) badgeCount = attenzionatiCount;
    if (isRilievoMisure) badgeCount = rilievoMisureCount;
    if (isCronoOrdini) badgeCount = cronoOrdiniScadutiCount;
    
    const showBadge = item.hasBadge && badgeCount > 0;
    const isUrgentBadge = isCronoOrdini && badgeCount > 0; // Badge rosso animato per ordini scaduti
    
    return (
      <Link
        to={item.path}
        onClick={onClick}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
          isActive 
            ? "bg-white/20 text-white shadow-sm" 
            : "text-white/80 hover:bg-white/10 hover:text-white"
        }`}
      >
        <div className="relative">
          <Icon className="w-5 h-5 flex-shrink-0" />
          {showBadge && (
            <span className={`absolute -top-1 -right-1 w-4 h-4 text-white text-xs rounded-full flex items-center justify-center ${
              isUrgentBadge ? "bg-red-500 animate-pulse" : "bg-orange-500"
            }`}>
              {badgeCount > 9 ? "9+" : badgeCount}
            </span>
          )}
        </div>
        {showLabel && (
          <span className="text-sm font-medium flex items-center gap-2">
            {item.label}
            {showBadge && (
              <span className={`px-1.5 py-0.5 text-white text-xs rounded-full min-w-[20px] text-center ${
                isUrgentBadge ? "bg-red-500 animate-pulse" : "bg-orange-500"
              }`}>
                {badgeCount}
              </span>
            )}
          </span>
        )}
      </Link>
    );
  };

  // =============================================
  // LAYOUT COMPATTO: Navbar orizzontale + Drawer
  // =============================================
  if (isCompactScreen) {
    return (
      <>
        {/* Navbar orizzontale fissa */}
        <nav className="fixed top-0 left-0 right-0 z-50 h-14 bg-gradient-to-r from-[#1e3a8a] to-[#1e40af] text-white px-4 flex items-center justify-between shadow-lg">
          {/* Left: Hamburger + Logo */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors bg-transparent"
              data-testid="navbar-menu-toggle"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            <Link to="/dashboard" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center overflow-hidden shadow">
                <img src="/assets/logo-ingenio.jpg" alt="Showroom Ingenio" className="w-full h-full object-contain" />
              </div>
              <span className="font-bold text-white hidden sm:inline">Showroom Ingenio</span>
            </Link>
          </div>

          {/* Right: Year Selector + Quick actions + User */}
          <div className="flex items-center gap-2">
            {/* Selettore Anno */}
            <YearSelector className="hidden sm:flex" />
            
            {/* Notifiche */}
            <div className="flex items-center">
              <NotificationBell />
            </div>
            
            {/* Avatar utente con dropdown */}
            <div className="flex items-center gap-2 pl-2 border-l border-white/20">
              <Avatar className="h-8 w-8 ring-2 ring-white/20">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback className="bg-[#f97316] text-white text-sm font-semibold">
                  {user?.name?.charAt(0) || "U"}
                </AvatarFallback>
              </Avatar>
              <span className="text-sm font-medium hidden md:inline max-w-[100px] truncate">
                {user?.name}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={logout}
                className="text-white/80 hover:text-white hover:bg-white/10 bg-transparent p-2"
                title="Esci"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </nav>

        {/* Overlay quando il drawer è aperto */}
        {isMenuOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-40 top-14"
            onClick={() => setIsMenuOpen(false)}
          />
        )}

        {/* Drawer laterale con menu */}
        <aside 
          className={`fixed top-14 left-0 bottom-0 w-64 bg-gradient-to-b from-[#1e3a8a] to-[#1e40af] text-white flex flex-col shadow-xl z-50 transform transition-transform duration-300 ${
            isMenuOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
          data-testid="sidebar-drawer"
        >
          {/* Selettore Anno (mobile) */}
          <div className="p-3 border-b border-white/10 sm:hidden">
            <YearSelector />
          </div>

          {/* Menu items */}
          <nav className="flex-1 overflow-y-auto p-3">
            <ul className="space-y-1">
              {allMenuItems.map((item) => (
                <li key={item.path}>
                  <MenuItem item={item} onClick={() => setIsMenuOpen(false)} />
                </li>
              ))}
            </ul>
          </nav>

          {/* Footer con info utente */}
          <div className="border-t border-white/10 p-4">
            <div className="flex items-center gap-3 mb-3">
              <Avatar className="h-9 w-9 ring-2 ring-white/20 flex-shrink-0">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback className="bg-[#f97316] text-white text-sm font-semibold">
                  {user?.name?.charAt(0) || "U"}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                <p className="text-xs text-white/60 truncate">{user?.email}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={logout}
              className="w-full justify-start text-white/80 hover:text-white hover:bg-white/10 bg-transparent"
              data-testid="logout-btn-drawer"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Esci
            </Button>
          </div>
        </aside>
      </>
    );
  }

  // =============================================
  // LAYOUT DESKTOP: Sidebar verticale collassabile
  // =============================================
  
  // Componente per menu item con tooltip (sidebar collassata)
  const SidebarMenuItem = ({ item }) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.path;
    const isMessages = item.path === "/messages";
    const isVerificaCompletamenti = item.path === "/verifica-completamenti";
    
    // Determina il badge count
    let badgeCount = 0;
    if (isMessages) badgeCount = 0; // TODO: unread messages counter non implementato
    if (isVerificaCompletamenti) badgeCount = verificaCompletamentiCount;
    
    const linkContent = (
      <Link
        to={item.path}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
          isCollapsed ? "justify-center" : ""
        } ${
          isActive 
            ? "bg-white/20 text-white shadow-sm" 
            : "text-white/80 hover:bg-white/10 hover:text-white"
        }`}
      >
        <div className="relative">
          <Icon className={`flex-shrink-0 ${isCollapsed ? "w-5 h-5" : "w-5 h-5"}`} />
          {badgeCount > 0 && (
            <span className={`absolute -top-1 -right-1 w-4 h-4 text-white text-xs rounded-full flex items-center justify-center ${
              isVerificaCompletamenti ? "bg-orange-500" : "bg-red-500"
            }`}>
              {badgeCount > 9 ? "9+" : badgeCount}
            </span>
          )}
        </div>
        {!isCollapsed && <span className="text-sm font-medium">{item.label}</span>}
      </Link>
    );

    if (isCollapsed) {
      return (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <li className="relative">{linkContent}</li>
          </TooltipTrigger>
          <TooltipContent side="right" className="bg-slate-900 text-white border-slate-700">
            {item.label}
          </TooltipContent>
        </Tooltip>
      );
    }

    return <li>{linkContent}</li>;
  };

  return (
    <TooltipProvider>
      <aside 
        className={`fixed left-0 top-0 bottom-0 bg-gradient-to-b from-[#1e3a8a] to-[#1e40af] text-white flex flex-col shadow-xl z-30 transition-all duration-300 ${
          isCollapsed ? "w-16" : "w-60"
        }`}
        data-testid="sidebar"
        data-collapsed={isCollapsed}
      >
        {/* Logo */}
        <div className={`border-b border-white/10 ${isCollapsed ? "p-3" : "p-4"}`}>
          <Link 
            to="/dashboard" 
            className={`flex items-center ${isCollapsed ? "justify-center" : "gap-3"}`}
          >
            <div className={`bg-white rounded-lg flex items-center justify-center overflow-hidden shadow-lg flex-shrink-0 ${isCollapsed ? "w-9 h-9" : "w-10 h-10"}`}>
              <img src="/assets/logo-ingenio.jpg" alt="Showroom Ingenio" className="w-full h-full object-contain" />
            </div>
            {!isCollapsed && (
              <div className="min-w-0">
                <h1 className="text-base font-bold text-white truncate">Showroom Ingenio</h1>
                <p className="text-xs text-[#fbbf24] font-medium">Gestionale AI</p>
              </div>
            )}
          </Link>
        </div>

        {/* Selettore Anno */}
        {!isCollapsed && (
          <div className="px-3 py-2 border-b border-white/10">
            <YearSelector />
          </div>
        )}

        {/* Toggle Button */}
        <button
          onClick={handleToggleClick}
          className={`absolute -right-3 top-20 w-6 h-6 bg-[#1e40af] border-2 border-white/20 rounded-full flex items-center justify-center text-white hover:bg-[#f97316] transition-colors shadow-lg z-50 ${showToggleHint ? 'animate-bounce-hint' : ''}`}
          title={isCollapsed ? "Espandi menu" : "Comprimi menu"}
          data-testid="sidebar-toggle"
        >
          {isCollapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronLeft className="w-3 h-3" />}
        </button>

        {/* Navigation */}
        <nav className={`flex-1 overflow-y-auto ${isCollapsed ? "p-2" : "p-3"}`}>
          <ul className="space-y-1">
            {allMenuItems.map((item) => (
              <SidebarMenuItem key={item.path} item={item} />
            ))}
          </ul>
        </nav>

        {/* Notifications */}
        <div className={`pb-3 ${isCollapsed ? "px-2" : "px-4"}`}>
          {isCollapsed ? (
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <div className="flex justify-center">
                  <NotificationBell />
                </div>
              </TooltipTrigger>
              <TooltipContent side="right" className="bg-slate-900 text-white border-slate-700">
                Firme Clienti
              </TooltipContent>
            </Tooltip>
          ) : (
            <div className="flex items-center gap-2 justify-center">
              <span className="text-sm font-medium text-white/90">Firme Clienti</span>
              <NotificationBell />
            </div>
          )}
        </div>

        {/* User section */}
        <div className={`border-t border-white/10 ${isCollapsed ? "p-2" : "p-4"}`}>
          {isCollapsed ? (
            <div className="flex flex-col items-center gap-2">
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Avatar className="h-9 w-9 ring-2 ring-white/20 cursor-pointer">
                    <AvatarImage src={user?.picture} alt={user?.name} />
                    <AvatarFallback className="bg-[#f97316] text-white text-sm font-semibold">
                      {user?.name?.charAt(0) || "U"}
                    </AvatarFallback>
                  </Avatar>
                </TooltipTrigger>
                <TooltipContent side="right" className="bg-slate-900 text-white border-slate-700">
                  <p className="font-medium">{user?.name}</p>
                  <p className="text-xs text-slate-400">{user?.email}</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={logout}
                    className="w-full justify-center text-white/80 hover:text-white hover:bg-white/10 bg-transparent p-2"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right" className="bg-slate-900 text-white border-slate-700">
                  Esci
                </TooltipContent>
              </Tooltip>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-3">
                <Avatar className="h-9 w-9 ring-2 ring-white/20 flex-shrink-0">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="bg-[#f97316] text-white text-sm font-semibold">
                    {user?.name?.charAt(0) || "U"}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                  <p className="text-xs text-white/60 truncate">{user?.email}</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={logout}
                className="w-full justify-start text-white/80 hover:text-white hover:bg-white/10 bg-transparent"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Esci
              </Button>
            </>
          )}
        </div>
      </aside>
    </TooltipProvider>
  );
}
